import React, { useMemo, useState } from 'react';
import { WEEKDAY_LABELS, buildSyntheticYearDays, simulateCapacityRange } from '../calculations/tankSimulation.js';
import { computeMonthlyNeed } from '../calculations/monthlyNeedCalculations.js';
import { effectiveRoofArea } from '../calculations/roofCollectionCalculations.js';
import SimLineChart from '../SimLineChart.js';
import ChartExportToolbar from '../ChartExportToolbar.js';

const WEEKDAY_LABELS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

/**
 * Étape 5 : répartition hebdomadaire type + paramètres de cuve + simulation journalière
 * sur une "année type" de 365 jours construite à partir des moyennes mensuelles.
 */
const TankSizingStep = ({
  usages, roofs, rainfall,
  weekdayPct, setWeekdayPct,
  tankParams, setTankParams,
  selectedTankVolume, selectedTankCoverage, onSelectTank,
  currentStep, totalSteps, goPrevious, goNext, canAdvance,
}) => {
  const [results, setResults] = useState(null);
  const [simError, setSimError] = useState(null);
  const [simInfo, setSimInfo] = useState(null);
  const [singleDay, setSingleDay] = useState(3); // Jeudi par défaut

  const effectiveArea = effectiveRoofArea(roofs);
  const weekdaySum = weekdayPct.reduce((a, b) => a + b, 0);

  const setWeekday = (i, value) => setWeekdayPct(prev => prev.map((v, idx) => idx === i ? value : v));

  const applyUniform7 = () => setWeekdayPct(Array(7).fill(100 / 7));
  const applyUniform5 = () => setWeekdayPct([20, 20, 20, 20, 20, 0, 0]); // Lun-Ven égaux, week-end à 0
  const applySingleDay = () => setWeekdayPct(Array.from({ length: 7 }, (_, i) => i === singleDay ? 100 : 0));

  const runSimulation = () => {
    setSimError(null);
    try {
      const need = computeMonthlyNeed(usages);
      const days = buildSyntheticYearDays(need, rainfall, weekdayPct);
      const { volMin, volMax, volStep, volInitial } = tankParams;
      const simResults = simulateCapacityRange(days, volMin, volMax, volStep || 1, effectiveArea, volInitial || 0);
      setResults(simResults);
      setSimInfo(`Simulation réalisée sur une année type de ${days.length} jours (surface utile ${effectiveArea.toFixed(1)} m²).`);
    } catch (err) {
      setSimError(err.message);
    }
  };

  const target = tankParams.targetReliability || 95;

  const chartData = useMemo(() => {
    if (!results) return null;
    const allPoints = results.map(r => ({ x: r.capacity, y: r.reliability * 100 }));
    const ys = allPoints.map(p => p.y);
    const dataMin = Math.min(...ys), dataMax = Math.max(...ys);
    const pad = Math.max(1, (dataMax - dataMin) * 0.08);
    const yMin = Math.max(0, Math.floor(dataMin - pad));
    const yMax = Math.min(100, Math.ceil(dataMax + pad));
    return { allPoints, yMin, yMax };
  }, [results]);

  const achieving = results?.find(r => r.reliability * 100 >= target);
  const best = results?.reduce((a, b) => (b.reliability > a.reliability ? b : a), results[0]);

  const getSimTSV = () => {
    if (!results) return '';
    const rows = ['Volume (m³)\tJours déficit\tDéficit (m³)\tDébordement (m³)\tDemande (m³)\tServi (m³)\tCouverture (%)'];
    results.forEach(r => rows.push(`${r.capacity.toFixed(2)}\t${r.deficitDays}\t${r.deficitTotal.toFixed(2)}\t${r.overflowTotal.toFixed(2)}\t${r.demandTotal.toFixed(2)}\t${r.servedTotal.toFixed(2)}\t${(r.reliability * 100).toFixed(1)}`));
    return rows.join('\n');
  };

  return (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">Récupération d'eau de pluie</span>
          <h2 className="apv2-step-heading">Dimensionnement de la cuve</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {totalSteps}</span>
      </div>
      <div className="apv2-step-body">

        <div className="rw-sub-label">Répartition hebdomadaire type</div>
        <p className="apv2-sim-hint">
          La simulation réutilise la pluviométrie mensuelle (étape 3, répartie uniformément sur les jours de chaque mois)
          et le besoin mensuel (étape 1). Indiquez, pour une semaine type, quelle part du besoin tombe chaque jour (en %) —
          la répartition est normalisée automatiquement, pas besoin de faire exactement 100%.
        </p>
        <div className="rw-weekday-presets">
          <button type="button" className="rw-preset-btn" onClick={applyUniform7}>Uniforme (7 jours)</button>
          <button type="button" className="rw-preset-btn" onClick={applyUniform5}>Uniforme hors week-end (Lun–Ven)</button>
          <span className="rw-preset-single">
            <button type="button" className="rw-preset-btn" onClick={applySingleDay}>Un seul jour :</button>
            <select value={singleDay} onChange={e => setSingleDay(+e.target.value)}>
              {WEEKDAY_LABELS_FULL.map((lbl, i) => <option key={i} value={i}>{lbl}</option>)}
            </select>
          </span>
        </div>
        <div className="rw-weekday-grid">
          {WEEKDAY_LABELS.map((lbl, i) => (
            <div key={i} className="rw-weekday-cell">
              <label>{lbl}</label>
              <input type="number" min="0" step="1" value={weekdayPct[i].toFixed(1)} onChange={e => setWeekday(i, +e.target.value || 0)} />
            </div>
          ))}
        </div>
        <p className="apv2-sim-hint">Somme : {weekdaySum.toFixed(1)}% (informatif — normalisé automatiquement)</p>

        <div className="rw-sub-label">Paramètres de la cuve</div>
        <p className="apv2-sim-hint">
          Surface de collecte utile reprise de l'étape 2 : <b>{effectiveArea.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m²</b>.
          Simulation sur une « année type » de 365 jours — elle ne représente pas une année réelle précise
          (pas de sécheresses ou d'années exceptionnelles).
        </p>
        <div className="rw-param-row">
          <div className="rw-param-field">
            <label>Volume min (m³)</label>
            <input type="number" step="0.5" value={tankParams.volMin} onChange={e => setTankParams(p => ({ ...p, volMin: +e.target.value }))} />
          </div>
          <div className="rw-param-field">
            <label>Volume max (m³)</label>
            <input type="number" step="0.5" value={tankParams.volMax} onChange={e => setTankParams(p => ({ ...p, volMax: +e.target.value }))} />
          </div>
          <div className="rw-param-field">
            <label>Pas (m³)</label>
            <input type="number" step="0.5" value={tankParams.volStep} onChange={e => setTankParams(p => ({ ...p, volStep: +e.target.value }))} />
          </div>
          <div className="rw-param-field">
            <label>Stock initial (m³)</label>
            <input type="number" step="0.1" value={tankParams.volInitial} onChange={e => setTankParams(p => ({ ...p, volInitial: +e.target.value }))} />
          </div>
          <div className="rw-param-field">
            <label>Couverture cible (%)</label>
            <input type="number" min="0" max="100" step="1" value={tankParams.targetReliability} onChange={e => setTankParams(p => ({ ...p, targetReliability: +e.target.value }))} />
          </div>
        </div>
        <button type="button" className="apv2-footer-btn-cta" onClick={runSimulation}>Lancer la simulation</button>
        {simError && <div className="rw-status rw-status--bad">{simError}</div>}
        {simInfo && !simError && <div className="rw-status rw-status--ok">{simInfo}</div>}

        {results && chartData && (
          <>
            <div className="rw-sub-label">Résultats — couverture selon le volume de cuve</div>
            <div className="rw-chart-block">
              <ChartExportToolbar containerId="rw-sim-chart" getDataTSV={getSimTSV} />
              <div id="rw-sim-chart">
                <SimLineChart points={chartData.allPoints} yMin={chartData.yMin} yMax={chartData.yMax} color="#0a4d5c"
                  targetY={target >= chartData.yMin && target <= chartData.yMax ? target : null} yUnit="%" />
              </div>
            </div>
            <p className="apv2-sim-hint">
              Axe recadré sur {chartData.yMin}–{chartData.yMax}% (plage réelle des couvertures obtenues) ;
              toutes les tailles de cuve testées ({chartData.allPoints[0].x.toFixed(1)}–{chartData.allPoints[chartData.allPoints.length - 1].x.toFixed(1)} m³) sont affichées.
            </p>
            <p className="apv2-sim-hint">
              {achieving
                ? `Volume minimal testé atteignant ${target}% de couverture sur une année type : ${achieving.capacity.toFixed(2)} m³.`
                : `Aucun volume testé n'atteint ${target}% de couverture (max obtenu : ${(best.reliability * 100).toFixed(1)}% pour ${best.capacity.toFixed(2)} m³). Élargis la plage de volumes.`}
            </p>

            <div className="apv2-flat-table-wrapper">
              <table className="apv2-flat-table">
                <thead>
                  <tr><th>Volume (m³)</th><th>Jours déficit</th><th>Déficit (m³)</th><th>Débord. (m³)</th><th>Demande (m³)</th><th>Servi (m³)</th><th>Couverture</th><th /></tr>
                </thead>
                <tbody>
                  {results.map(r => (
                    <tr key={r.capacity} className={selectedTankVolume === r.capacity ? 'apv2-flat-table-row--selected' : ''}>
                      <td>{r.capacity.toFixed(2)}</td>
                      <td>{r.deficitDays}</td>
                      <td>{r.deficitTotal.toFixed(2)}</td>
                      <td>{r.overflowTotal.toFixed(2)}</td>
                      <td>{r.demandTotal.toFixed(2)}</td>
                      <td>{r.servedTotal.toFixed(2)}</td>
                      <td>{(r.reliability * 100).toFixed(1)}%</td>
                      <td><button type="button" className="rw-add-btn" onClick={() => onSelectTank(r.capacity, r.reliability)}>Choisir cette cuve</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {selectedTankVolume != null && (
              <p className="apv2-sim-hint">
                Cuve retenue : {selectedTankVolume.toFixed(2)} m³ (couverture {selectedTankCoverage != null ? (selectedTankCoverage * 100).toFixed(1) : '?'}%) — passage à l'estimation du budget.
              </p>
            )}
          </>
        )}
      </div>
      <div className="apv2-step-footer">
        <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
          ← Précédent
        </button>
        <button type="button" className="apv2-footer-btn-cta" onClick={goNext} disabled={!canAdvance}>
          Suivant →
        </button>
      </div>
    </div>
  );
};

export default TankSizingStep;
